/**
 * SpeakableSpecification Schema Component
 * Optimizes content for voice search (Google Assistant, Siri, Alexa)
 * Marks specific CSS selectors as suitable for text-to-speech
 */

import { useEffect } from "react";

interface SpeakableSchemaProps {
  pageUrl?: string;
  pageName?: string;
  cssSelectors?: string[];
  xpathSelectors?: string[];
}

const SpeakableSchema = ({
  pageUrl = "https://www.gosolar.pro/",
  pageName = "California Solar + Battery Storage",
  cssSelectors = [
    ".hero-headline",
    ".hero-subheadline", 
    ".faq-question",
    ".faq-answer",
    ".key-takeaway",
    ".quotable-snippet",
    ".featured-snippet-box",
    ".answer-box"
  ],
  xpathSelectors
}: SpeakableSchemaProps) => {

  useEffect(() => {
    const speakableSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      "name": pageName,
      "url": pageUrl,
      "speakable": {
        "@type": "SpeakableSpecification",
        ...(cssSelectors && cssSelectors.length > 0 && { "cssSelector": cssSelectors }),
        ...(xpathSelectors && xpathSelectors.length > 0 && { "xpath": xpathSelectors })
      },
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://www.gosolar.pro/#website",
        "name": "California Energy Group",
        "url": "https://www.gosolar.pro"
      }
    };

    const scriptId = `speakable-schema-${pageUrl.replace(/[^a-z0-9]/gi, '-')}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(speakableSchema);

    return () => {
      const toRemove = document.getElementById(scriptId);
      if (toRemove) toRemove.remove();
    };
  }, [pageUrl, pageName, cssSelectors, xpathSelectors]);

  return null;
};

// Homepage speakable configuration
export const HomepageSpeakable = () => (
  <SpeakableSchema
    pageUrl="https://www.gosolar.pro/"
    pageName="California Solar + Battery $ Zero Down"
    cssSelectors={[
      ".hero-headline",
      ".hero-subheadline",
      "#faq .faq-question",
      "#faq .faq-answer",
      ".trust-badge-text",
      ".how-it-works-step"
    ]}
  />
);

// FAQ page speakable configuration
export const FAQSpeakable = () => (
  <SpeakableSchema
    pageUrl="https://www.gosolar.pro/#faq"
    pageName="California Solar FAQ"
    cssSelectors={[
      ".solar-faq .faq-question",
      ".solar-faq .faq-answer",
      "[itemtype='https://schema.org/Question'] [itemprop='name']",
      "[itemtype='https://schema.org/Answer'] [itemprop='text']"
    ]}
  />
);

// Blog article speakable configuration
export const BlogSpeakable = ({ slug, title }: { slug: string; title: string }) => (
  <SpeakableSchema
    pageUrl={`https://www.gosolar.pro/blog/${slug}`}
    pageName={title}
    cssSelectors={[
      "article h1",
      "article .excerpt",
      "article .key-takeaways",
      "article .featured-snippet-box",
      ".expert-quote"
    ]}
  />
);

// Learning center speakable
export const LearningSpeakable = () => (
  <SpeakableSchema
    pageUrl="https://www.gosolar.pro/learning-center"
    pageName="California Solar Learning Center"
    cssSelectors={[
      ".learning-module-title",
      ".learning-module-description",
      ".course-overview"
    ]}
  />
);

// Equipment page speakable
export const EquipmentSpeakable = ({ product, url }: { product: string; url: string }) => (
  <SpeakableSchema
    pageUrl={url}
    pageName={product}
    cssSelectors={[
      ".product-title",
      ".product-description",
      ".spec-highlight",
      ".comparison-summary"
    ]}
  />
);

export default SpeakableSchema;
